import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Utensils, Package, DollarSign, Clock, Check, X, Loader2, 
  RefreshCw, Store, MapPin, ArrowRight, Wrench, AlertCircle,
  Sparkles, Download
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FoodDeliveryAdmin() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [generatingParts, setGeneratingParts] = useState(false);
  
  // Food Delivery State
  const [restaurants, setRestaurants] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState('');
  
  // Auto Parts State
  const [autoPartsStores, setAutoPartsStores] = useState([]);
  const [autoPartsAds, setAutoPartsAds] = useState([]);
  const [partsCategories, setPartsCategories] = useState([]);
  const [partsCount, setPartsCount] = useState(100);
  
  // Stats
  const [foodStats, setFoodStats] = useState({ restaurants: 0, items: 0 });
  const [partsStats, setPartsStats] = useState({ stores: 0, ads: 0 });

  const fetchFoodData = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const [restaurantsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/food-delivery/restaurants`, { headers, withCredentials: true }),
        axios.get(`${API}/auto-parts/categories`, { headers, withCredentials: true })
      ]);
      
      setRestaurants(restaurantsRes.data.restaurants || []);
      setPartsCategories(categoriesRes.data.categories || []);
      setFoodStats({ restaurants: (restaurantsRes.data.restaurants || []).length, items: 0 });
    } catch (error) {
      console.error('Failed to fetch food data:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchAutoPartsData = useCallback(async () => {
    try {
      const headers = { ...getAuthHeaders() };
      const [storesRes, adsRes] = await Promise.all([
        axios.get(`${API}/auto-parts/stores`, { headers, withCredentials: true }),
        axios.get(`${API}/auto-parts/ads?limit=20`, { headers, withCredentials: true })
      ]);
      
      setAutoPartsStores(storesRes.data.stores || []);
      setAutoPartsAds(adsRes.data.ads || []);
      setPartsStats({ 
        stores: (storesRes.data.stores || []).length, 
        ads: adsRes.data.count || (adsRes.data.ads || []).length 
      });
    } catch (error) {
      console.error('Failed to fetch auto parts data:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchFoodData();
    fetchAutoPartsData();
  }, [fetchFoodData, fetchAutoPartsData]);

  const handleScrapeMcDonalds = async () => {
    setScraping(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await axios.post(
        `${API}/food-delivery/scrape-restaurant`,
        { restaurant: 'mcdonalds' },
        { headers, withCredentials: true }
      );
      
      toast.success(`Scraped ${response.data.items_uploaded} menu items from McDonald's!`);
      fetchFoodData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scrape restaurant');
    } finally {
      setScraping(false);
    }
  };

  const handleScrapeAutoPartsStores = async () => {
    setScraping(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await axios.post(
        `${API}/auto-parts/scrape-stores`,
        {},
        { headers, withCredentials: true }
      );
      
      toast.success(`Added ${response.data.stores_added} auto parts stores in Fort Wayne, IN!`);
      fetchAutoPartsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scrape stores');
    } finally {
      setScraping(false);
    }
  };

  const handleGenerateAutoPartsAds = async () => {
    setGeneratingParts(true);
    try {
      const headers = { 'Content-Type': 'application/json', ...getAuthHeaders() };
      const response = await axios.post(
        `${API}/auto-parts/generate-ads`,
        { count: partsCount },
        { headers, withCredentials: true }
      );
      
      toast.success(`Generated ${response.data.ads_generated} auto parts ads!`);
      fetchAutoPartsData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate ads');
    } finally {
      setGeneratingParts(false);
    }
  };

  const fetchRestaurantMenu = async (restaurantId) => {
    try {
      const headers = { ...getAuthHeaders() };
      const response = await axios.get(`${API}/food-delivery/menu/${restaurantId}`, { headers, withCredentials: true });
      setMenuItems(response.data.categories || {});
      setFoodStats(prev => ({ ...prev, items: response.data.total_items || 0 }));
    } catch (error) {
      toast.error('Failed to fetch menu');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <Tabs defaultValue="food-delivery" className="space-y-4">
      <TabsList className="grid grid-cols-2 w-full max-w-md">
        <TabsTrigger value="food-delivery" className="flex items-center gap-2">
          <Utensils className="h-4 w-4" />
          Food Delivery
        </TabsTrigger>
        <TabsTrigger value="auto-parts" className="flex items-center gap-2">
          <Wrench className="h-4 w-4" />
          Auto Parts
        </TabsTrigger>
      </TabsList>

      {/* Food Delivery Tab */}
      <TabsContent value="food-delivery" className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-primary">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Store className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Restaurants</p>
                  <p className="text-2xl font-bold">{foodStats.restaurants}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-success">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Utensils className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Menu Items</p>
                  <p className="text-2xl font-bold">{foodStats.items}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Min Markup</p>
                  <p className="text-2xl font-bold text-blue-600">$8.00</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scrape Action */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              AI Menu Scraper
            </CardTitle>
            <CardDescription>
              Scrape real menu data with $8 minimum markup + sales tax pricing
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleScrapeMcDonalds}
                disabled={scraping}
                className="bg-orange-600 hover:bg-orange-700 gap-2"
              >
                {scraping ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Scraping...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    Scrape McDonald's (Fort Wayne, IN)
                  </>
                )}
              </Button>
            </div>
            
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Pricing Rule:</strong> All items are priced at Menu Price + $8.00 Markup + 7% Sales Tax.
                This ensures minimum $8 profit per item.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Restaurants List */}
        {restaurants.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Active Restaurants</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {restaurants.map((restaurant) => (
                  <div 
                    key={restaurant.restaurant_id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                    onClick={() => {
                      setSelectedRestaurant(restaurant.restaurant_id);
                      fetchRestaurantMenu(restaurant.restaurant_id);
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {restaurant.logo && (
                        <img src={restaurant.logo} alt={restaurant.name} className="w-10 h-10 object-contain" />
                      )}
                      <div>
                        <p className="font-medium">{restaurant.name}</p>
                        <p className="text-sm text-gray-500">{restaurant.address}</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-100 text-green-700">
                      Active
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Menu Items Preview */}
        {selectedRestaurant && Object.keys(menuItems).length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Menu Preview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {Object.entries(menuItems).map(([category, items]) => (
                  <div key={category}>
                    <h4 className="font-semibold text-lg mb-3">{category}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {items.slice(0, 4).map((item) => (
                        <div key={item.item_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                          {item.image && (
                            <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{item.name}</p>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="text-gray-400 line-through">{formatCurrency(item.menu_price)}</span>
                              <ArrowRight className="h-3 w-3 text-gray-400" />
                              <span className="text-green-600 font-bold">{formatCurrency(item.customer_price)}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>

      {/* Auto Parts Tab */}
      <TabsContent value="auto-parts" className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="glass-primary">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Store className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Stores</p>
                  <p className="text-2xl font-bold">{partsStats.stores}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-success">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Package className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Product Ads</p>
                  <p className="text-2xl font-bold">{partsStats.ads}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-yellow-100 rounded-lg">
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">Approval Required</p>
                  <p className="text-sm font-medium text-yellow-600">Charge AFTER admin approves</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scrape Stores */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5 text-blue-500" />
              Scrape Auto Parts Stores
            </CardTitle>
            <CardDescription>
              Add AutoZone, O'Reilly, Advance Auto Parts, and NAPA stores in Fort Wayne, IN
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleScrapeAutoPartsStores}
              disabled={scraping}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              {scraping ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Scraping Stores...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4" />
                  Scrape Fort Wayne Stores
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Generate Ads */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-yellow-500" />
              Generate Auto Parts Ads
            </CardTitle>
            <CardDescription>
              Generate realistic product listings from scraped auto parts retailers
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="space-y-2">
                <label className="text-sm font-medium">Number of Ads</label>
                <Input 
                  type="number"
                  min={10}
                  max={1000}
                  value={partsCount}
                  onChange={(e) => setPartsCount(Math.min(1000, Math.max(10, parseInt(e.target.value) || 100)))}
                  className="w-32"
                />
              </div>
              <Button 
                onClick={handleGenerateAutoPartsAds}
                disabled={generatingParts}
                className="bg-green-600 hover:bg-green-700 gap-2"
              >
                {generatingParts ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" />
                    Generate {partsCount} Ads
                  </>
                )}
              </Button>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                <strong>Workflow:</strong> Customers order auto parts → Admin approves order → Provider assigned → 
                Customer is charged → Provider picks up from store → Delivered to customer
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Stores List */}
        {autoPartsStores.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Scraped Stores</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {autoPartsStores.map((store) => (
                  <div 
                    key={store.store_id}
                    className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                  >
                    {store.logo && (
                      <img src={store.logo} alt={store.name} className="w-10 h-10 object-contain" />
                    )}
                    <div>
                      <p className="font-medium">{store.name}</p>
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {store.address}
                      </p>
                      <p className="text-xs text-gray-400">{store.hours}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Sample Ads */}
        {autoPartsAds.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Sample Product Ads</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {autoPartsAds.slice(0, 6).map((ad) => (
                  <div key={ad.ad_id} className="border rounded-lg overflow-hidden">
                    {ad.image && (
                      <img src={ad.image} alt={ad.product_name} className="w-full h-32 object-cover" />
                    )}
                    <div className="p-3">
                      <p className="font-medium text-sm line-clamp-2">{ad.product_name}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">{ad.category}</Badge>
                        <Badge variant="outline" className="text-xs">{ad.brand}</Badge>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-400 line-through">{formatCurrency(ad.store_price)}</span>
                        <span className="text-green-600 font-bold">{formatCurrency(ad.customer_price)}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{ad.store_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </TabsContent>
    </Tabs>
  );
}
